import { Link } from "react-router-dom";
import { useLayoutEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const has = (roles) => (u) => roles.includes(u?.role);

export const NAV_ITEMS = [
  { to: "/catalogue", id: "catalogue", label: "Catalogue" },
  { to: "/nos-offres", id: "offres", label: "Nos offres" },
  { to: "/videos-whatsapp", id: "videos-whatsapp", label: "Vidéos WhatsApp" },
  { to: "/#categories", id: "categories", label: "Catégories", anchor: true },
  { to: "/#engagements", id: "engagements", label: "Nos engagements", anchor: true, desktopOnly: true },
  { to: "/expertises", id: "expertises", label: "Nos Expertises" },
  { to: "/developpement-informatique", id: "dev", label: "Recherche & Développement" },
  { to: "/prendre-rendez-vous", id: "rdv", label: "Prendre RDV" },
  { to: "/webinaires", id: "webinaires", label: "Webinaires" },
  { to: "/cours", id: "cours", label: "Formations" },
  { to: "/recherche-scientifique", id: "recherche", label: "Recherche Scientifique" },
  { to: "/atelier-ecriture", id: "atelier", label: "Atelier d'écriture" },
  { to: "/demander-demo", id: "demo", label: "Démo SIH", desktopOnly: true },
  { to: "/pilotage", id: "pilotage", label: "Tableau de bord hospitalier", show: has(["gestionnaire", "admin"]) },
  { to: "/pilotage-sih", id: "pilotage-sih", label: "Pilotage du SIH", show: has(["medecin_dim", "tim", "gestionnaire_donnees", "admin"]) },
  { to: "/pmsi", id: "pmsi", label: "PMSI & Couverture", show: has(["medecin_dim", "tim", "gestionnaire_donnees", "dms", "gestionnaire", "comptable", "financier", "admin"]) },
  { to: "/direction", id: "direction", label: "Direction Générale", show: has(["directeur_general", "directeur_medical", "admin"]) },
  { to: "/qualite", id: "qualite", label: "Qualité patient", show: (u) => ["dms", "medecin_dim"].includes(u?.role) || ["DMS", "DIM"].includes(u?.direction) },
  { to: "/courriers", id: "courriers", label: "Courriers", show: (u) => Boolean(u?.direction) || ["directeur_general", "directeur_medical", "admin", "dms", "comptable", "financier", "gestionnaire", "medecin_dim"].includes(u?.role) },
  { to: "/admin", id: "admin", label: "Tableau de bord", show: has(["admin"]) },
];

export const visibleNavItems = (user, mobile = false) =>
  NAV_ITEMS.filter((it) => (!it.show || it.show(user)) && !(mobile && it.desktopOnly));

function NavItem({ item, className, testidSuffix = "", onClick }) {
  const props = { "data-testid": `nav-${item.id}${testidSuffix}`, className, onClick };
  return item.anchor ? <a href={item.to} {...props}>{item.label}</a> : <Link to={item.to} {...props}>{item.label}</Link>;
}

const MORE_W = 96;

export function DesktopNav({ user, navCls }) {
  const items = visibleNavItems(user);
  const navRef = useRef(null);
  const measureRef = useRef(null);
  const [visible, setVisible] = useState(items.length);

  useLayoutEffect(() => {
    const nav = navRef.current, meas = measureRef.current;
    if (!nav || !meas) return;
    const compute = () => {
      const widths = [...meas.children].map((c) => c.offsetWidth + 2);
      const total = widths.reduce((a, b) => a + b, 0);
      const avail = nav.clientWidth;
      if (total <= avail) return setVisible(widths.length);
      let used = MORE_W, n = 0;
      for (const w of widths) { if (used + w > avail) break; used += w; n++; }
      setVisible(n);
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(nav);
    return () => ro.disconnect();
  }, [items.length]);

  const shown = items.slice(0, visible), overflow = items.slice(visible);
  const overflowActive = overflow.some((it) => navCls(it.to).includes("nav-link-active"));

  return (
    <nav ref={navRef} className="relative mx-3 hidden h-full min-w-0 flex-1 items-center gap-0.5 overflow-hidden text-sm font-medium text-slate-600 md:flex" data-testid="main-nav">
      <div ref={measureRef} aria-hidden className="invisible absolute left-0 top-0 flex items-center gap-0.5 whitespace-nowrap">
        {items.map((it) => <span key={it.id} className="nav-link">{it.label}</span>)}
      </div>
      {shown.map((it) => <NavItem key={it.id} item={it} className={`${navCls(it.to)} whitespace-nowrap`} />)}
      {overflow.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="nav-more" className={`nav-link flex items-center gap-1 whitespace-nowrap${overflowActive ? " nav-link-active" : ""}`}>
              Plus <ChevronDown size={14} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64" data-testid="nav-more-menu">
            {overflow.map((it) => (
              <DropdownMenuItem key={it.id} asChild className="cursor-pointer">
                <NavItem item={it} testidSuffix="-more" className={navCls(it.to).includes("nav-link-active") ? "font-semibold text-[#0369A1]" : ""} />
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </nav>
  );
}

export function MobileNav({ user, onClose }) {
  return (
    <nav className="border-t border-slate-200 bg-white px-6 py-4 md:hidden" data-testid="nav-mobile-panel">
      <div className="flex flex-col gap-3 text-sm font-medium text-slate-600">
        {visibleNavItems(user, true).map((it) => <NavItem key={it.id} item={it} testidSuffix="-mobile" onClick={onClose} />)}
      </div>
    </nav>
  );
}
